import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import {
  FiActivity,
  FiBarChart2,
  FiCheckCircle,
  FiRefreshCw,
  FiTarget,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";

import axios from "../../utills/api";

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [sourceStats, setSourceStats] = useState([]);

  const [dateRange, setDateRange] = useState("all");

  /* =========================================================
     FETCH DATA
  ========================================================= */

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError("");

      const [leadsResponse, campaignsResponse, sourcesResponse] =
        await Promise.all([
          axios.get("/leads"),
          axios.get("/campaigns"),
          axios.get("/lead-sources/stats"),
        ]);

      const leadData = leadsResponse?.data?.data;
      const campaignData = campaignsResponse?.data?.data;
      const sourceData = sourcesResponse?.data?.data;

      let leadList = [];

      if (Array.isArray(leadData)) {
        leadList = leadData;
      } else if (Array.isArray(leadData?.leads)) {
        leadList = leadData.leads;
      }

      let campaignList = [];

      if (Array.isArray(campaignData)) {
        campaignList = campaignData;
      } else if (Array.isArray(campaignData?.campaigns)) {
        campaignList = campaignData.campaigns;
      }

      let sourceList = [];

      if (Array.isArray(sourceData)) {
        sourceList = sourceData;
      } else if (Array.isArray(sourceData?.sources)) {
        sourceList = sourceData.sources;
      }

      setLeads(leadList);
      setCampaigns(campaignList);
      setSourceStats(sourceList);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Analytics fetch error:", err);

      setError(
        err?.response?.data?.message ||
          "Unable to load analytics data."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  /* =========================================================
     DATE FILTER
  ========================================================= */

  const filteredLeads = useMemo(() => {
    if (dateRange === "all") {
      return leads;
    }

    const now = new Date();
    const cutoff = new Date(now);

    if (dateRange === "7") {
      cutoff.setDate(now.getDate() - 7);
    }

    if (dateRange === "30") {
      cutoff.setDate(now.getDate() - 30);
    }

    if (dateRange === "90") {
      cutoff.setDate(now.getDate() - 90);
    }

    return leads.filter((lead) => {
      const createdAt =
        lead?.createdAt ||
        lead?.created_at ||
        lead?.date;

      if (!createdAt) {
        return false;
      }

      return new Date(createdAt) >= cutoff;
    });
  }, [leads, dateRange]);

  /* =========================================================
     METRICS
  ========================================================= */

  const metrics = useMemo(() => {
    const total = filteredLeads.length;

    const converted = filteredLeads.filter(
      (lead) =>
        String(lead?.status || "").toLowerCase() ===
        "converted"
    ).length;

    const applications = filteredLeads.filter(
      (lead) =>
        lead?.applicationId ||
        lead?.application ||
        String(lead?.status || "").toLowerCase() ===
          "application"
    ).length;

    const admissions = filteredLeads.filter(
      (lead) =>
        lead?.admissionId ||
        lead?.admission ||
        String(lead?.status || "").toLowerCase() ===
          "admitted"
    ).length;

    const conversionRate =
      total > 0
        ? ((converted / total) * 100).toFixed(1)
        : "0.0";

    return {
      total,
      converted,
      applications,
      admissions,
      conversionRate,
    };
  }, [filteredLeads]);

  /* =========================================================
     STATUS BREAKDOWN
  ========================================================= */

  const statusStats = useMemo(() => {
    const map = {};

    filteredLeads.forEach((lead) => {
      const status = lead?.status || "Unknown";

      map[status] = (map[status] || 0) + 1;
    });

    return Object.entries(map)
      .map(([status, count]) => ({
        status,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredLeads]);

  /* =========================================================
     SOURCE BREAKDOWN
  ========================================================= */

  const calculatedSourceStats = useMemo(() => {
    const map = {};

    filteredLeads.forEach((lead) => {
      const source =
        lead?.source ||
        lead?.leadSource ||
        "Other";

      map[source] = (map[source] || 0) + 1;
    });

    return Object.entries(map)
      .map(([source, count]) => ({
        source,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredLeads]);

  /* =========================================================
     MONTHLY TREND
  ========================================================= */

  const monthlyStats = useMemo(() => {
    const map = {};

    filteredLeads.forEach((lead) => {
      const dateValue =
        lead?.createdAt ||
        lead?.created_at ||
        lead?.date;

      if (!dateValue) {
        return;
      }

      const date = new Date(dateValue);

      if (Number.isNaN(date.getTime())) {
        return;
      }

      const key = date.toLocaleDateString("en-IN", {
        month: "short",
        year: "numeric",
      });

      map[key] = (map[key] || 0) + 1;
    });

    return Object.entries(map).slice(-12);
  }, [filteredLeads]);

  /* =========================================================
     CAMPAIGN METRICS
  ========================================================= */

  const campaignStats = useMemo(() => {
    return [...campaigns]
      .sort(
        (a, b) =>
          Number(b?.leadCount || 0) -
          Number(a?.leadCount || 0)
      )
      .slice(0, 10);
  }, [campaigns]);

  const maxMonthlyLeads = Math.max(
    ...monthlyStats.map(([, count]) => count),
    1
  );

  /* =========================================================
     HELPERS
  ========================================================= */

  const formatNumber = (value) =>
    Number(value || 0).toLocaleString("en-IN");

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

  const statusBadgeClass = (status) => {
    const value = String(status || "").toLowerCase();

    if (value === "converted") {
      return "bg-success-subtle text-success";
    }

    if (
      value === "qualified" ||
      value === "quoted"
    ) {
      return "bg-info-subtle text-info";
    }

    if (
      value === "lost" ||
      value === "cancelled"
    ) {
      return "bg-danger-subtle text-danger";
    }

    if (
      value === "follow-up" ||
      value === "followup"
    ) {
      return "bg-warning-subtle text-warning";
    }

    return "bg-primary-subtle text-primary";
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col>
            <h4 className="mb-1">
              Analytics
            </h4>

            <p className="text-muted mb-0">
              Track lead generation, sources,
              conversions and campaign performance.
            </p>
          </Col>

          <Col
            xs="12"
            md="auto"
            className="mt-3 mt-md-0"
          >
            <div className="d-flex gap-2 align-items-end">
              <div>
                <Label className="form-label mb-1">
                  Period
                </Label>

                <Input
                  type="select"
                  value={dateRange}
                  onChange={(event) =>
                    setDateRange(event.target.value)
                  }
                >
                  <option value="all">
                    All Time
                  </option>
                  <option value="7">
                    Last 7 Days
                  </option>
                  <option value="30">
                    Last 30 Days
                  </option>
                  <option value="90">
                    Last 90 Days
                  </option>
                </Input>
              </div>

              <Button
                color="light"
                onClick={fetchAnalytics}
                disabled={loading}
              >
                <FiRefreshCw
                  size={15}
                  className="me-1"
                />
                Refresh
              </Button>
            </div>
          </Col>
        </Row>

        {/* Alerts */}
        {error && (
          <Alert
            color="danger"
            toggle={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {/* Last updated */}
        {lastUpdated && !loading && (
          <div className="text-muted small mb-3">
            Last updated:{" "}
            {lastUpdated.toLocaleTimeString(
              "en-IN"
            )}
          </div>
        )}

        {/* KPI CARDS */}
        <Row>
          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <FiUsers />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Total Leads
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(metrics.total)}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <FiCheckCircle />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Converted Leads
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        metrics.converted
                      )}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <FiTarget />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Conversion Rate
                    </p>

                    <h4 className="mb-0">
                      {metrics.conversionRate}%
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <FiTrendingUp />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Admissions
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        metrics.admissions
                      )}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {loading ? (
          <Card>
            <CardBody className="text-center py-5">
              <Spinner className="me-2" />
              Loading analytics...
            </CardBody>
          </Card>
        ) : (
          <>
            {/* MONTHLY TREND */}
            <Row>
              <Col xl="8">
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 className="card-title mb-1">
                          Lead Trend
                        </h5>

                        <p className="text-muted mb-0">
                          Monthly lead generation
                        </p>
                      </div>

                      <FiBarChart2
                        size={20}
                        className="text-muted"
                      />
                    </div>

                    {monthlyStats.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        No lead trend data available.
                      </div>
                    ) : (
                      <div className="d-flex align-items-end gap-3 overflow-auto px-2">
                        {monthlyStats.map(
                          ([month, count]) => {
                            const height =
                              Math.max(
                                10,
                                (count /
                                  maxMonthlyLeads) *
                                  220
                              );

                            return (
                              <div
                                key={month}
                                className="text-center"
                                style={{
                                  minWidth: "70px",
                                }}
                              >
                                <div
                                  className="bg-primary rounded-top mx-auto"
                                  style={{
                                    width: "42px",
                                    height: `${height}px`,
                                  }}
                                  title={`${count} leads`}
                                />

                                <div className="fw-medium mt-2">
                                  {count}
                                </div>

                                <div className="text-muted small">
                                  {month}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              {/* STATUS */}
              <Col xl="4">
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-1">
                      Lead Status
                    </h5>

                    <p className="text-muted mb-4">
                      Current lead distribution
                    </p>

                    {statusStats.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        No status data available.
                      </div>
                    ) : (
                      statusStats.map(
                        ({ status, count }) => {
                          const percentage =
                            metrics.total > 0
                              ? (
                                  (count /
                                    metrics.total) *
                                  100
                                ).toFixed(1)
                              : 0;

                          return (
                            <div
                              key={status}
                              className="mb-3"
                            >
                              <div className="d-flex justify-content-between mb-1">
                                <Badge
                                  className={statusBadgeClass(
                                    status
                                  )}
                                >
                                  {status}
                                </Badge>

                                <span className="text-muted small">
                                  {count} (
                                  {percentage}%)
                                </span>
                              </div>

                              <div
                                className="progress"
                                style={{
                                  height: "6px",
                                }}
                              >
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${percentage}%`,
                                  }}
                                />
                              </div>
                            </div>
                          );
                        }
                      )
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* SOURCE + CAMPAIGNS */}
            <Row>
              <Col xl="6">
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-1">
                      Lead Sources
                    </h5>

                    <p className="text-muted mb-4">
                      Where your leads are coming from
                    </p>

                    <div className="table-responsive">
                      <Table
                        hover
                        className="align-middle mb-0"
                      >
                        <thead className="table-light">
                          <tr>
                            <th>Source</th>
                            <th>Leads</th>
                            <th>Share</th>
                          </tr>
                        </thead>

                        <tbody>
                          {calculatedSourceStats.length ===
                          0 ? (
                            <tr>
                              <td
                                colSpan="3"
                                className="text-center text-muted py-4"
                              >
                                No source data available.
                              </td>
                            </tr>
                          ) : (
                            calculatedSourceStats.map(
                              (item) => {
                                const share =
                                  metrics.total >
                                  0
                                    ? (
                                        (item.count /
                                          metrics.total) *
                                        100
                                      ).toFixed(1)
                                    : 0;

                                return (
                                  <tr
                                    key={
                                      item.source
                                    }
                                  >
                                    <td>
                                      <span className="fw-medium">
                                        {
                                          item.source
                                        }
                                      </span>
                                    </td>

                                    <td>
                                      {
                                        item.count
                                      }
                                    </td>

                                    <td>
                                      {share}%
                                    </td>
                                  </tr>
                                );
                              }
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl="6">
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-1">
                      Campaign Performance
                    </h5>

                    <p className="text-muted mb-4">
                      Campaigns generating the most
                      leads
                    </p>

                    <div className="table-responsive">
                      <Table
                        hover
                        className="align-middle mb-0"
                      >
                        <thead className="table-light">
                          <tr>
                            <th>Campaign</th>
                            <th>Platform</th>
                            <th>Leads</th>
                            <th>Spend</th>
                          </tr>
                        </thead>

                        <tbody>
                          {campaignStats.length ===
                          0 ? (
                            <tr>
                              <td
                                colSpan="4"
                                className="text-center text-muted py-4"
                              >
                                No campaign data
                                available.
                              </td>
                            </tr>
                          ) : (
                            campaignStats.map(
                              (campaign) => (
                                <tr
                                  key={
                                    campaign?._id ||
                                    campaign?.id
                                  }
                                >
                                  <td>
                                    <div className="fw-medium">
                                      {
                                        campaign?.name
                                      }
                                    </div>

                                    <div className="text-muted small">
                                      {
                                        campaign?.code
                                      }
                                    </div>
                                  </td>

                                  <td>
                                    <Badge className="bg-primary-subtle text-primary">
                                      {campaign?.platform ||
                                        "Other"}
                                    </Badge>
                                  </td>

                                  <td>
                                    {formatNumber(
                                      campaign?.leadCount
                                    )}
                                  </td>

                                  <td>
                                    {formatCurrency(
                                      campaign?.spend
                                    )}
                                  </td>
                                </tr>
                              )
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* CONVERSION SUMMARY */}
            <Row>
              <Col xl="12">
                <Card>
                  <CardBody>
                    <div className="d-flex align-items-center mb-4">
                      <div className="avatar-sm flex-shrink-0">
                        <div className="avatar-title bg-primary-subtle text-primary rounded">
                          <FiActivity />
                        </div>
                      </div>

                      <div className="ms-3">
                        <h5 className="card-title mb-1">
                          Funnel Summary
                        </h5>

                        <p className="text-muted mb-0">
                          Lead progression overview
                        </p>
                      </div>
                    </div>

                    <Row className="text-center">
                      <Col md="3">
                        <div className="border rounded p-3">
                          <div className="text-muted mb-2">
                            Leads
                          </div>

                          <h4 className="mb-0">
                            {formatNumber(
                              metrics.total
                            )}
                          </h4>
                        </div>
                      </Col>

                      <Col md="3">
                        <div className="border rounded p-3">
                          <div className="text-muted mb-2">
                            Applications
                          </div>

                          <h4 className="mb-0">
                            {formatNumber(
                              metrics.applications
                            )}
                          </h4>
                        </div>
                      </Col>

                      <Col md="3">
                        <div className="border rounded p-3">
                          <div className="text-muted mb-2">
                            Converted
                          </div>

                          <h4 className="mb-0">
                            {formatNumber(
                              metrics.converted
                            )}
                          </h4>
                        </div>
                      </Col>

                      <Col md="3">
                        <div className="border rounded p-3">
                          <div className="text-muted mb-2">
                            Admissions
                          </div>

                          <h4 className="mb-0">
                            {formatNumber(
                              metrics.admissions
                            )}
                          </h4>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default Analytics;